import type { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import { CloseHealth, closeHealthLabels } from "@common/types/closeHealth";
import {
    buildRolesByUserId,
    deduplicateMembers,
} from "@components/organizationView/clients-closes/cells/closeOverviewMemberHelpers";
import { formatCloseTemplateDateRange } from "@components/settings/OrganizationCloseTemplates/formatCloseTemplateDateRange";
import type {
    DataGridPeopleMember,
    DataGridPersonValue,
} from "@design-system/DataGrid";
import { getAppliedByMember } from "../cells/useAppliedByMember";
import { getWorkTaskProgressPercentage } from "../cells/WorkTaskProgressCell";
import type {
    ClientsWorkOverviewRow,
    ClientsWorkOverviewWorkRow,
} from "../clientsWorkOverviewViewConfig";
import { getWorkMemberName } from "../editing/workItemEditing";
import { daysInStage } from "../workRowCompare";
import {
    getWorkFinishedState,
    WORK_FINISHED_STATE_TITLES,
} from "../workStageGroups";

type WorkRow = ClientsWorkOverviewWorkRow;

/** Client and group rows are label rows, so only work rows have values. */
export const workValue =
    <TValue>(read: (row: WorkRow) => TValue) =>
    (row: ClientsWorkOverviewRow) =>
        row.rowType === "work" ? read(row) : undefined;

/** The ID right-clicking the Service name copies: the client's workspace, or the work's entity. */
export const getServiceCopyId = (row: ClientsWorkOverviewRow) => {
    switch (row.rowType) {
        case "workspace":
            return row.workspaceId;
        case "work":
            return String(row.entityId);
        default:
            return undefined;
    }
};

export const getServiceCopyLabel = (row: ClientsWorkOverviewRow) =>
    row.rowType === "workspace" ? "Workspace" : "Entity";

export const getServicePeriod = ({ startDate, endDate }: WorkRow) =>
    startDate || endDate
        ? formatCloseTemplateDateRange(startDate, endDate)
        : null;

/** Done or Canceled for finished work, else the stage's name. */
export const getStage = (row: WorkRow) => {
    const finishedState = getWorkFinishedState(row);
    return finishedState
        ? WORK_FINISHED_STATE_TITLES[finishedState]
        : row.closeStageName;
};

/** "3 d" from when the stage was set until `now`; nothing for finished work. */
export const getDaysInStage = ({ row, now }: { row: WorkRow; now: Date }) => {
    const days = daysInStage(row, now);
    return days === null ? null : `${days} d`;
};

/** Applied by for work whose author isn't a member; its cell shows the name without an avatar. */
export const UNKNOWN_APPLIED_BY: DataGridPersonValue = { name: "Unknown" };

/** Who applied the work, `UNKNOWN_APPLIED_BY` when not a member; nothing until members load. */
export const toAppliedBy = ({
    row,
    members,
}: {
    row: WorkRow;
    members: readonly OrganizationMember[] | undefined;
}): DataGridPersonValue | undefined => {
    if (row.createdByUserId === null) {
        return undefined;
    }
    const appliedBy = getAppliedByMember({
        members,
        userId: row.createdByUserId,
    });
    if (!appliedBy) {
        return undefined;
    }
    return appliedBy.member
        ? {
              name: appliedBy.name,
              avatarUrl: appliedBy.member.user.avatar ?? undefined,
          }
        : UNKNOWN_APPLIED_BY;
};

export const getHealthLabel = ({ health }: WorkRow) =>
    health === CloseHealth.NONE ? null : closeHealthLabels[health];

/** The share of finished tasks, 0 to 100; nothing for work without tasks. */
export const getWorkProgress = (row: WorkRow) =>
    row.totalTaskCount > 0 ? getWorkTaskProgressPercentage(row) : undefined;

/** The remaining minutes; nothing for work with no time remaining or done. */
export const getWorkEstimatedTime = ({
    estimatedTimeRemaining,
    estimatedTimeDone,
}: WorkRow) =>
    estimatedTimeRemaining === 0 && estimatedTimeDone === 0
        ? undefined
        : estimatedTimeRemaining;

/** Preparers, reviewers, and managers, each once, with their roles on the work. */
export const toWorkTeamMembers = (
    row: ClientsWorkOverviewRow,
): DataGridPeopleMember[] => {
    if (row.rowType !== "work") {
        return [];
    }
    const { preparers, reviewers, managers } = row;
    const rolesByUserId = buildRolesByUserId(preparers, reviewers, managers);
    return deduplicateMembers([...preparers, ...reviewers, ...managers]).map(
        (member) => ({
            name: getWorkMemberName(member),
            avatarUrl: member.avatar ?? undefined,
            roles: rolesByUserId.get(member.userId),
        }),
    );
};
